import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux';

import PageTitle from './PageTitle';
import ErrorDisplay from './ErrorDisplay';
import Waiting from './Waiting';

import Paper from '@mui/material/Paper';

import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';

import clsx from 'clsx';
import useStyles from '../styles/Styles'

import { makeRequest, resetErrorMessage, resetLoginFailure } from '../actions';

import _ from 'lodash';
import * as yup from 'yup';
import { isTrue } from '../utils';

const Login= () => {
	const dispatch = useDispatch();
	const generalSettings = useSelector(state => state.settings.general);
	const loginError = useSelector(state => state.requests.login_error);
	
	const canLogin = (generalSettings.data.umas_status === 2 || (generalSettings.data.umas_status === 1 && isTrue(generalSettings.data.early_enrol_on) && isTrue(generalSettings.data.enrolling_on)));
	
	const classes = useStyles();
	
	const schema = yup.object().shape({
		user: yup.string().matches(/^([0-9]+|[^\s@]+@[^\s@]+\.[^\s@]+)$/, { message: 'Please enter either your member number or your email address'}),
		password: yup.string().required('Please enter your password'),
	});
	
	const [data, setData] = useState({
		user: '',
		password: ''
	})
	
	const [validator, setValidator] = useState({
		user: {
			error: false,
			message: ''
		},
		password: {
			error: false,
			message: ''
		}
	});
	
	const [showPassword, setShowPassword] = useState(false);
	
	const fields = [
		{
			id: 'user',
			atts: {
				label: "Member no. or email address",
				value: data.user,
			}
		},
		{
			id: 'password',
			atts: {
				label: "Password",
				type: showPassword ? "text" : "password",
				value: data.password,
				InputProps: {
					endAdornment: (
					  <InputAdornment position="end">
						<IconButton
						  aria-label="toggle password visibility"
						  onClick={() => setShowPassword(!showPassword)}
						  onMouseDown={(event) => event.preventDefault()}
						  edge="end"
						>
						{showPassword ? <Visibility /> : <VisibilityOff />}
						</IconButton>
					  </InputAdornment>
					)
				}
			}
		},
	];
	
	const storeAndValidate = (key, value) => {
		setData({...data, [key]: value})

		schema.validateAt(key, {[key]: value})
		.then(val => {
			setValidator({...validator, [key]: {error: false, message: ''}})
		})
		.catch(err => {
			console.log(err);
			setValidator({...validator, [key]: {error: true, message: err.message}})
		})
	}
	
	const [submitting, setSubmitting] = useState(false);
	
	useEffect(() => {
		if(loginError){
			setSubmitting(false);
		}
	}, [loginError])
	
	const maybeSubmit = (event) => {
		event.preventDefault();
		
		dispatch(resetErrorMessage());
		dispatch(resetLoginFailure());
		
		schema.validate(data, {abortEarly: false})
		.then(value => {
			setSubmitting(true);
			dispatch(makeRequest('login', data));
		}).catch(err => {
			const v = _.cloneDeep(validator);
			
			_.each(err.inner, (e) => {
				v[e.path].error = true;
				v[e.path].message = e.message;
			})
			
			setValidator(v);
		})
	}
	
	const HelpLinks = () => (
	<>
	<div className={classes.sectionTitle}>
		<Typography variant="h5" component="h2">Help links</Typography>
	</div>
	 <Grid container>
            <Grid item sm={12} md={6}>
			<List className={classes.contentList} >
			<ListItem>
			<ListItemAvatar>
			  <Avatar>
			  <HelpOutlineIcon/>
			  </Avatar>
			</ListItemAvatar>
			<ListItemText color="primary"
				primary="Forgot password?"
				secondary={<Link to="/login/reset-password">Reset password</Link>}
				/>
			</ListItem>
			<ListItem>
			<ListItemAvatar>
			  <Avatar>
			  <HelpOutlineIcon/>
			  </Avatar>
			</ListItemAvatar>
			<ListItemText color="primary"
				primary="Forgot member number?"
				secondary={<Link to="/login/retrieve-member-no">Retrieve member number</Link>}
				/>
			</ListItem>
			<ListItem>
			<ListItemAvatar>
			  <Avatar>
			  <HelpOutlineIcon/>
			  </Avatar>
			</ListItemAvatar>
			<ListItemText color="primary"
				primary="Forgot all login details?"
				secondary={<Link to="/login/retrieve-member-details">Retrieve member details</Link>}
				/>
			</ListItem>
			</List>
            </Grid>
            <Grid item sm={12} md={6}>
			<List className={classes.contentList} >
			<ListItem>
			<ListItemAvatar>
			  <Avatar>
			  <PersonAddIcon/>
			  </Avatar>
			</ListItemAvatar>
			<ListItemText color="primary"
				primary="Not a member yet?"
				secondary={<Link to="/join">Join up</Link>}
				/>
			</ListItem>
			</List>

            </Grid>
          </Grid>
	</>
	);
	
	return (
		<>
		<ErrorDisplay />
		
		<Paper className={clsx(classes.innerContent, classes.page)} square={true}>
		<PageTitle title='Log in' />
		
		{canLogin ?
		( submitting ? 
		<Waiting message='Logging in...' /> :
		<>
			<form className={classes.form} noValidate> 
			<Grid container spacing={2}>
			{fields.map((f, i) => (
				<Grid item xs={12} key={i} >
				<TextField 
					variant="outlined"
					required
					fullWidth
					error={validator[f.id].error}
					helperText={validator[f.id].message}
					onChange={(event) => storeAndValidate(f.id, event.target.value )}
					{...f.atts}
					/>
				</Grid>
			))}
				<Grid item xs={12}>
				<Button
					type="submit"
					fullWidth
					variant="contained"
					color="primary"
					className={classes.submit}
					onClick={(event) => maybeSubmit(event)}
				  >Submit
				  </Button>
				</Grid>
			</Grid>
			  
			</form>
			<HelpLinks />
		</> ) : 
		( generalSettings.expires === 0 ?
		<Waiting message='Loading. Please wait..' /> :
		<>
		<Typography variant="h6">
		Login is off
		</Typography>
		<Typography variant="body2">
		Sorry, logging in is currently disabled. Please check again later, or <Link to="/contact">contact us</Link> for more information.
		</Typography>
		</>
		)}
		</Paper>
		</>
	)};

export default Login